/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { Flex, Spinner } from '@radix-ui/themes'
import { createFileRoute } from '@tanstack/react-router'
import { practiceTestService } from '@/services/practiceTestService'
import { questionService } from '@/services/questionService'
import { pickCaseStudyQuestionsByQuestionIds } from '@/utils/case-study-questions'
import {
  getPracticeTestNextPage,
  mergePracticeTestPages,
} from '@/utils/practice-test-pages'
import { ClassStatus } from '@/types/class'
import { PracticeTestDetail, PracticeTestStatus } from '@/types/practice-test'
import CreateMockExam from '@/features/create-mock-exam'
import MockExamDetail from '@/features/create-mock-exam/detail'

export const Route = createFileRoute(
  '/_authenticated/create-mock-exam/detail/$ptId/$moduleId/$classId'
)({
  component: CreateOrDetailWrapper,
})

function CreateOrDetailWrapper() {
  const { ptId, moduleId, classId } = Route.useParams() as {
    ptId: string
    moduleId: string
    classId: string
  }
  const [page, setPage] = useState(1)

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['practiceTestDetail', ptId, 'paged', page],
    queryFn: () =>
      practiceTestService.getPracticeTest({
        practice_test_id: ptId,
        size: 10,
        page: page,
      }),
    placeholderData: keepPreviousData,
  })

  const status = pageData?.status?.toLowerCase()?.trim()

  const {
    data: detailPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchDetail,
  } = useInfiniteQuery({
    queryKey: ['practiceTestDetail', ptId],
    enabled: status === PracticeTestStatus.DRAFT,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      practiceTestService.getPracticeTest({
        practice_test_id: ptId,
        size: 10,
        page: pageParam as number,
      }),
    getNextPageParam: getPracticeTestNextPage,
  })

  const pt = mergePracticeTestPages(
    detailPages?.pages as PracticeTestDetail[]
  )

  const practiceTestDetailData =
    status === PracticeTestStatus.DRAFT ? pt : pageData
  const totalsFromDetail = (() => {
    const sections = practiceTestDetailData?.sections || []
    let totalTime = 0
    let totalMarks = 0
    let totalQuestions = 0
    for (const s of sections) {
      totalTime += s.total_time || 0
      totalMarks += s.total_marks || 0
      totalQuestions += s.total_questions || 0
    }
    return { totalTime, totalMarks, totalQuestions }
  })()

  const { data: draftCaseStudyGroups } = useQuery({
    queryKey: ['draft-case-study-questions', ptId, pt?.sections, detailPages],
    enabled: status === PracticeTestStatus.DRAFT,
    queryFn: async () => {
      const sections = pt?.sections || []
      const result: Record<string, any[]> = {}
      for (const section of sections) {
        const sectionName = section.name
        const csData = section.case_studies?.data as any
        const caseStudies = Array.isArray(csData)
          ? csData
          : csData
            ? [csData]
            : []
        const groups = await Promise.all(
          caseStudies.map(async (cs: any) => {
            try {
              const resp = await questionService.getQuestionByCaseStudy(
                cs.case_study_id
              )
              // Normalize list from possible shapes
              const list =
                (resp as any)?.questions ??
                (resp as any)?.question ??
                (resp as any)?.data ??
                []
              const items = pickCaseStudyQuestionsByQuestionIds(
                Array.isArray(list) ? list : [],
                cs.question_ids
              )
              return {
                case_study_id: cs.case_study_id,
                items,
                total_time: cs.total_time ?? cs.total_duration ?? 0,
                total_marks: cs.total_marks ?? cs.total_mark ?? 0,
                total_questions: items.length,
              }
            } catch (_err) {
              return {
                case_study_id: cs.case_study_id,
                items: [],
                total_time: cs.total_time ?? cs.total_duration ?? 0,
                total_marks: cs.total_marks ?? cs.total_mark ?? 0,
                total_questions: 0,
              }
            }
          })
        )
        result[sectionName] = groups
      }
      return result
    },
  })

  const [isViewOnly, setIsViewOnly] = useState<boolean>(false)

  useEffect(() => {
    if (
      pt?.class_status === ClassStatus.deactivated &&
      (pt?.status === PracticeTestStatus.DRAFT ||
        pt?.status === PracticeTestStatus.INACTIVE)
    ) {
      setIsViewOnly(true)
    }
  }, [pt])

  useEffect(() => {
    if (
      status === PracticeTestStatus.DRAFT &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage()
    }
  }, [status, hasNextPage, fetchNextPage, isFetchingNextPage])

  const initialDataForEdit = (() => {
    const sections = pt?.sections || []
    const initialSelectedQuestions: Record<string, any[]> = {}
    const initialSelectedCaseStudies: Record<string, any[]> = {}

    for (const section of sections) {
      const sectionName = section.name
      initialSelectedQuestions[sectionName] =
        (section.questions?.data as any[]) || []
      const csData = section.case_studies?.data as any
      const fallback = Array.isArray(csData) ? csData : csData ? [csData] : []
      initialSelectedCaseStudies[sectionName] =
        (draftCaseStudyGroups as any)?.[sectionName] || fallback
    }

    return { initialSelectedQuestions, initialSelectedCaseStudies }
  })()
  
  if (isFetching && !pageData) {
    return (
      <Flex
        justify='center'
        align='center'
        className='h-[calc(100vh-200px)] w-full'
      >
        <Spinner />
      </Flex>
    )
  }

  if (status === PracticeTestStatus.DRAFT)
    return (
      <CreateMockExam
        initialPracticeTestId={ptId}
        initialPracticeTestName={pt?.pt_name}
        initialGeneralInformation={pt?.general_information}
        status={pt?.status}
        moduleId={moduleId}
        classId={classId}
        initDuration={pt?.duration}
        initialDurationMinutes={totalsFromDetail.totalTime}
        initialTotalQuestions={totalsFromDetail.totalQuestions}
        initialTotalMarks={totalsFromDetail.totalMarks}
        initialSelectedQuestions={initialDataForEdit.initialSelectedQuestions}
        initialSelectedCaseStudies={
          initialDataForEdit.initialSelectedCaseStudies
        }
        refetch={refetchDetail}
        isViewOnly={isViewOnly}
      />
    )
  return (
    <MockExamDetail
      practiceTestDetailData={practiceTestDetailData}
      isViewOnly={isViewOnly}
      setPage={setPage}
      isLoading={isFetching}
    />
  )
}

export default Route
